import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { Autocomplete, TextField, TablePagination } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
    FaChevronDown,
    FaTimes,
    FaExclamationTriangle,
    FaUserCircle,
    FaHashtag,
    FaCalendarAlt,
    FaTasks,
    FaFileAlt,
    FaBriefcase,
    FaBuilding,
    FaExternalLinkAlt,
    FaHistory,
    FaChevronUp,
    FaFilter
} from "react-icons/fa";
import { FaRegCircleCheck } from "react-icons/fa6";
import { fetchActiveMemberData } from "../../Redux/Slice/TaskMasterSlice";
import {
    DayReportFindforcheq,
    FindIdDayReportCreate,
    DayReportFindforcheqReport
} from "../../Redux/Slice/Day";
import Loader from "../../Components/Loader/Loader";
import { AllTaskReport } from "./AllTaskReport";
import socket from "../../socket";
import ErrorBoundary from "../../Components/Common/ErrorBoundary";

const TaskTimeingReportContent = () => {
    const dispatch = useDispatch();

    // Redux selectors
    const { ActiveMemberData } = useSelector((state) => state.TaskMaster || {});
    const {
        DayReportFindforcheqData,
        DayReportFindforcheqLoading,
        DayReportFindforcheqReportData,
        DayReportFindforcheqReportLoading
    } = useSelector((state) => state.Day || {});

    // Filters and local state
    const [selectedMember, setSelectedMember] = useState(null);
    const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
    const [startDateInvalid, setStartDateInvalid] = useState(false);
    const [endDateInvalid, setEndDateInvalid] = useState(false);
    const [filterDateType, setFilterDateType] = useState("GeneratedDate");
    const [taskData, setTaskData] = useState([]);
    const [remarks, setRemarks] = useState({});
    const [submittingIds, setSubmittingIds] = useState(new Set());

    // Modal State
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedCallDetails, setSelectedCallDetails] = useState(null);
    const [expandedDays, setExpandedDays] = useState({});

    // Tab State
    const [activeTab, setActiveTab] = useState("tasks");
    const [showFilters, setShowFilters] = useState(false);

    // Refresh trigger for AllTaskReport
    const [refreshActivity, setRefreshActivity] = useState(0);

    // Pagination for Main Task Table
    const [taskPage, setTaskPage] = useState(0);
    const [taskRowsPerPage, setTaskRowsPerPage] = useState(10);

    // Clean socket listener with debouncing to avoid multiple rapid re-fetches
    useEffect(() => {
        let debounceTimer = null;
        const handleNewDayReport = (data) => {
            console.log("Real-time update received in TaskTimeingReport:", data);
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                dispatch(DayReportFindforcheq());
                setRefreshActivity((prev) => prev + 1);
            }, 300);
        };

        socket.on("newDayReport", handleNewDayReport);

        return () => {
            clearTimeout(debounceTimer);
            socket.off("newDayReport", handleNewDayReport);
        };
    }, [dispatch]);

    // Initial Data Fetch
    useEffect(() => {
        dispatch(fetchActiveMemberData());
        dispatch(DayReportFindforcheq());
    }, [dispatch]);

    // Format & merge incoming Task Data safely
    useEffect(() => {
        if (Array.isArray(DayReportFindforcheqData?.data)) {
            const rawList = DayReportFindforcheqData.data.filter(Boolean);

            // Pre-calculate timestamps and formatted dates once to avoid repeated moment() in sort/filter/render
            const parsedList = rawList.map((item) => {
                const rawDate = item?.GeneratedDate;
                let timestamp = 0;
                let dateStr = "";
                let displayDate = "-";
                let displayTime = "";

                if (rawDate) {
                    const parsed = moment(rawDate);
                    if (parsed.isValid()) {
                        timestamp = parsed.valueOf();
                        dateStr = parsed.utc().format("YYYY-MM-DD");
                        displayDate = parsed.format("MMM DD, YYYY");
                        displayTime = parsed.utc().format("hh:mm A");
                    }
                }

                const callNoVal = item?.WorkId?.CallNo != null ? `${item.WorkId.CallNo}` : "-";
                const company =
                    typeof item?.WorkId?.CompanyName === "object"
                        ? item?.WorkId?.CompanyName?.WhaClientName || item?.WorkId?.CompanyName?.CompanyName
                        : item?.WorkId?.CompanyName;

                return {
                    id: item?._id,
                    timestamp,
                    dateStr,
                    displayDate,
                    displayTime,
                    memberName: item?.Memberid?.Name || "Unknown Member",
                    GenerateDate: displayDate,
                    companyName: company || "-",
                    callNo: callNoVal,
                    description: item?.Description || "-",
                    technical: item?.WorkId?.SeniorDevloperStatus || "Pending",
                    verifyStatus: "verify",
                    action: "submit",
                    remark: "",
                    profilePhoto: item?.Memberid?.ProfilePhoto || null,
                    originalData: item
                };
            });

            // Fast numeric timestamp sort
            parsedList.sort((a, b) => b.timestamp - a.timestamp);

            // Merge with existing state to avoid losing in-progress verifications on live refresh
            setTaskData((prevData) => {
                if (!prevData || prevData.length === 0) return parsedList;
                const prevMap = new Map(prevData.map((t) => [t.id, t]));
                return parsedList.map((newItem) => {
                    const existing = prevMap.get(newItem.id);
                    if (existing) {
                        return {
                            ...newItem,
                            verifyStatus: existing.verifyStatus || newItem.verifyStatus,
                            technical: existing.technical || newItem.technical,
                            action: existing.action || newItem.action,
                            remark: existing.remark || newItem.remark
                        };
                    }
                    return newItem;
                });
            });
        }
    }, [DayReportFindforcheqData]);

    // Reset pagination on filter change
    useEffect(() => {
        setTaskPage(0);
    }, [startDate, endDate, selectedMember]);

    /* ================= MEMBER LIST ================= */
    const uniqueMemberOptions = useMemo(() => {
        if (!Array.isArray(ActiveMemberData?.data)) return [];
        const opts = ActiveMemberData.data.map((member) => ({
            Name_id: member?._id || member?.id,
            Name_name: member?.Name || member?.name || member?.MemberName || "Unnamed Member",
        }));
        return Array.from(new Map(opts.map((m) => [m.Name_id, m])).values()).sort((a, b) =>
            (a.Name_name || "").localeCompare(b.Name_name || "")
        );
    }, [ActiveMemberData]);

    /* ================= FILTERED TASKS (HIGH-PERFORMANCE MEMO) ================= */
    const filteredTasks = useMemo(() => {
        if (!Array.isArray(taskData)) return [];

        return taskData.filter((task) => {
            if (selectedMember && task.memberName !== selectedMember.Name_name) return false;
            if (startDateInvalid || endDateInvalid) return false;

            const taskDateStr = task.dateStr;
            if (!taskDateStr) return !startDate && !endDate;

            if (startDate && endDate) {
                return taskDateStr >= startDate && taskDateStr <= endDate;
            } else if (startDate) {
                return taskDateStr >= startDate;
            } else if (endDate) {
                return taskDateStr <= endDate;
            }
            return true;
        });
    }, [taskData, selectedMember, startDate, endDate, startDateInvalid, endDateInvalid]);

    // Count pending
    const pendingCount = useMemo(() => {
        return filteredTasks.filter((task) => task.verifyStatus !== "verified").length;
    }, [filteredTasks]);

    // Safe Page clamp to prevent MUI TablePagination out-of-bounds crash
    const totalCount = filteredTasks.length;
    const maxPage = Math.max(0, Math.ceil(totalCount / taskRowsPerPage) - 1);
    const safeTaskPage = taskPage > maxPage ? 0 : taskPage;

    const handleChangeTaskPage = (event, newPage) => {
        setTaskPage(newPage);
    };

    const handleChangeTaskRowsPerPage = (event) => {
        setTaskRowsPerPage(parseInt(event.target.value, 10));
        setTaskPage(0);
    };

    const handleVerifyClick = (id) => {
        setTaskData((prevData) =>
            prevData.map((task) =>
                task.id === id
                    ? {
                        ...task,
                        verifyStatus: task.verifyStatus === "verified" ? "verify" : "verified"
                    }
                    : task
            )
        );
    };

    const handleSubmit = async (id) => {
        if (submittingIds.has(id)) return;

        const taskToSubmit = taskData.find((task) => task.id === id);
        if (!taskToSubmit) return;

        setSubmittingIds((prev) => new Set(prev).add(id));

        const currentRemark = remarks[id] || "";
        const { GeneratedDate, ...restOfData } = taskToSubmit.originalData || {};

        const payload = {
            id: taskToSubmit.id,
            ...restOfData,
            WorkId: taskToSubmit?.originalData?.WorkId?._id || taskToSubmit?.originalData?.WorkId,
            Memberid: taskToSubmit?.originalData?.Memberid?._id || taskToSubmit?.originalData?.Memberid,
            GeneratedDate: taskToSubmit?.originalData?.GeneratedDate,
            Tec: taskToSubmit?.technical === "Verified",
            Phys: taskToSubmit?.verifyStatus === "verified",
            MainDescription: taskToSubmit?.description,
            Remark: currentRemark,
        };

        try {
            const result = await dispatch(FindIdDayReportCreate(payload));

            if (result.meta?.requestStatus === "fulfilled") {
                toast.success(result.payload?.message || "Task submitted successfully!");
                // Immediately remove submitted task from local list so UI feels instant
                setTaskData((prevData) => prevData.filter((task) => task.id !== id));
                dispatch(DayReportFindforcheq());
            } else {
                console.error("Submission failed:", result.payload);
                toast.error(result.payload || "Failed to submit task.");
            }
        } finally {
            setSubmittingIds((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    };

    const handleRemarkChange = (id, value) => {
        setRemarks((prev) => ({
            ...prev,
            [id]: value
        }));
    };

    const handleCallClick = (task) => {
        const memberId = task.originalData?.Memberid?._id || task.originalData?.Memberid;
        const workId = task.originalData?.WorkId?._id || task.originalData?.WorkId;

        if (memberId && workId) {
            dispatch(DayReportFindforcheqReport({ Memberid: memberId, WorkId: workId }));
            setModalOpen(true);
        } else {
            console.warn("Missing Member ID or Work ID");
        }
    };

    const closeModal = () => {
        setModalOpen(false);
        setSelectedCallDetails(null);
    };

    useEffect(() => {
        if (DayReportFindforcheqReportData && modalOpen) {
            setSelectedCallDetails(DayReportFindforcheqReportData);

            if (Array.isArray(DayReportFindforcheqReportData?.data) && DayReportFindforcheqReportData.data.length > 0) {
                const grouped = DayReportFindforcheqReportData.data.reduce((acc, curr) => {
                    const date = curr?.GeneratedDate && moment(curr.GeneratedDate).isValid()
                        ? moment(curr.GeneratedDate).utc().format("YYYY-MM-DD")
                        : "unknown";
                    if (!acc[date]) acc[date] = [];
                    acc[date].push(curr);
                    return acc;
                }, {});

                const dates = Object.keys(grouped)
                    .filter((d) => d !== "unknown")
                    .sort((a, b) => moment(b).diff(moment(a)));

                if (dates.length > 0) {
                    setExpandedDays({ [dates[0]]: true });
                }
            }
        }
    }, [DayReportFindforcheqReportData, modalOpen]);

    const toggleDay = (date) => {
        setExpandedDays((prev) => ({
            ...prev,
            [date]: !prev[date]
        }));
    };

    // Helper to group data by date safely
    const getGroupedActivities = () => {
        if (!Array.isArray(selectedCallDetails?.data)) return {};

        return selectedCallDetails.data.reduce((acc, curr) => {
            if (!curr) return acc;
            const date = curr?.GeneratedDate && moment(curr.GeneratedDate).isValid()
                ? moment(curr.GeneratedDate).utc().format("YYYY-MM-DD")
                : "unknown";
            if (!acc[date]) acc[date] = [];
            acc[date].push(curr);
            return acc;
        }, {});
    };

    const getRowColor = (verifyStatus) => {
        return verifyStatus === "verified" ? "bg-green-50 hover:bg-green-100" : "bg-red-50 hover:bg-red-100";
    };

    const getVerifyButtonColor = (verifyStatus) => {
        return verifyStatus === "verified"
            ? "bg-green-500 hover:bg-green-600 text-white"
            : "bg-red-500 hover:bg-red-600 text-white";
    };

    const getVerifyButtonText = (verifyStatus) => {
        return verifyStatus === "verified" ? "Verified" : "Verify";
    };

    // Initial loader
    if (DayReportFindforcheqLoading && !DayReportFindforcheqData) {
        return <Loader />;
    }

    return (
        <div className="space-y-4">
            <h3 className="text-2xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
                Task Timing Report
            </h3>

            {/* Collapsible Filter Section */}
            <div className="mt-1 bg-white shadow-sm border border-gray-200 rounded-xl mb-6 overflow-hidden">
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="w-full px-5 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100/70 transition-colors border-none outline-none cursor-pointer text-left"
                >
                    <div className="flex items-center gap-2.5">
                        <FaFilter className="text-[#133A69] text-xs" />
                        <span className="font-bold text-slate-800 text-sm">Filter Reports</span>
                        {(selectedMember || startDate || endDate) && (
                            <span className="bg-[#133A69]/10 text-[#133A69] px-2.5 py-0.5 rounded-full text-[10px] font-black tracking-wide border border-[#133A69]/10 uppercase ml-2">
                                Active Filters
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 font-semibold">
                            {showFilters ? "Hide Filters" : "Show Filters"}
                        </span>
                        {showFilters ? (
                            <FaChevronUp className="text-gray-500 text-xs" />
                        ) : (
                            <FaChevronDown className="text-gray-500 text-xs" />
                        )}
                    </div>
                </button>

                {showFilters && (
                    <div className="p-4 border-t border-gray-100 bg-white">
                        <div className="grid grid-cols-12 gap-4 items-end">
                            <div className="col-span-12 md:col-span-6 lg:col-span-3">
                                <label className="font-semibold block mb-2 text-sm">Member Name</label>
                                <Autocomplete
                                    options={uniqueMemberOptions}
                                    value={selectedMember}
                                    onChange={(event, newValue) => setSelectedMember(newValue)}
                                    getOptionLabel={(option) => option?.Name_name || ""}
                                    isOptionEqualToValue={(option, value) =>
                                        option?.Name_id === value?.Name_id
                                    }
                                    popupIcon={<FaChevronDown className="text-gray-600 text-sm" />}
                                    clearIcon={<FaTimes className="text-gray-600 text-xs" />}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Select Member"
                                            size="small"
                                            className="bg-white rounded-md custom-input"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: <>{params.InputProps.endAdornment}</>,
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {activeTab === "activity" && (
                                <div className="col-span-12 md:col-span-6 lg:col-span-3">
                                    <label className="font-semibold block mb-2 text-sm">Filter Date By</label>
                                    <select
                                        value={filterDateType}
                                        onChange={(e) => setFilterDateType(e.target.value)}
                                        className="w-full px-3 py-2.5 bg-white border border-gray-400 rounded-md focus:outline-none focus:ring-1 focus:ring-[#133A69] focus:border-[#133A69] text-sm shadow-sm transition-all h-[40px]"
                                    >
                                        <option value="GeneratedDate">Generated Date</option>
                                        <option value="CheckdDate">Check Date</option>
                                    </select>
                                </div>
                            )}
                            <div className="col-span-12 md:col-span-6 lg:col-span-3 relative pb-[1px]">
                                <div className="flex justify-between items-center mb-2">
                                    <label className="font-semibold text-sm">From Date</label>
                                    {startDate && (
                                        <button
                                            onClick={() => {
                                                setStartDate("");
                                                setStartDateInvalid(false);
                                            }}
                                            className="text-xs text-red-500 hover:text-red-700 font-bold uppercase tracking-wider bg-transparent border-none outline-none cursor-pointer"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => {
                                        setStartDate(e.target.value);
                                        setStartDateInvalid(e.target.validity?.badInput || false);
                                    }}
                                    className={`w-full px-3 py-2.5 bg-white border ${startDateInvalid ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-gray-400 focus:ring-blue-500 focus:border-blue-500"} rounded-md focus:outline-none focus:ring-1 text-sm shadow-sm transition-all h-[40px]`}
                                />
                                {startDateInvalid && (
                                    <span className="text-red-500 text-[11px] mt-1 block font-medium absolute top-full left-0">
                                        <FaExclamationTriangle className="inline mr-1 mb-0.5" />
                                        Please enter a valid date.
                                    </span>
                                )}
                            </div>
                            <div className="col-span-12 md:col-span-6 lg:col-span-3 relative pb-[1px]">
                                <div className="flex justify-between items-center mb-2">
                                    <label className="font-semibold text-sm">To Date</label>
                                    {endDate && (
                                        <button
                                            onClick={() => {
                                                setEndDate("");
                                                setEndDateInvalid(false);
                                            }}
                                            className="text-xs text-red-500 hover:text-red-700 font-bold uppercase tracking-wider bg-transparent border-none outline-none cursor-pointer"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                                <input
                                    type="date"
                                    value={endDate}
                                    min={startDate}
                                    onChange={(e) => {
                                        setEndDate(e.target.value);
                                        setEndDateInvalid(e.target.validity?.badInput || false);
                                    }}
                                    className={`w-full px-3 py-2.5 bg-white border ${endDateInvalid ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-gray-400 focus:ring-blue-500 focus:border-blue-500"} rounded-md focus:outline-none focus:ring-1 text-sm shadow-sm transition-all h-[40px]`}
                                />
                                {endDateInvalid && (
                                    <span className="text-red-500 text-[11px] mt-1 block font-medium absolute top-full left-0">
                                        <FaExclamationTriangle className="inline mr-1 mb-0.5" />
                                        Please enter a valid date.
                                    </span>
                                )}
                            </div>
                            <div className="col-span-12 md:col-span-6 lg:col-span-3 pb-[1px]">
                                <button
                                    onClick={() => {
                                        const today = moment().format("YYYY-MM-DD");
                                        setStartDate(today);
                                        setEndDate(today);
                                        setStartDateInvalid(false);
                                        setEndDateInvalid(false);
                                    }}
                                    className="w-full px-4 py-2.5 bg-[#133A69] text-white font-medium rounded-md shadow-sm hover:bg-[#1a3b61] transition-colors h-[40px] flex items-center justify-center gap-2"
                                >
                                    <FaCalendarAlt size={14} />
                                    Today
                                </button>
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mt-4 pt-4 border-t border-gray-100">
                            <div className="text-sm font-medium text-gray-600">
                                <div className="flex gap-2 mb-2 sm:mb-0">
                                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-bold ring-1 ring-blue-500/20">
                                        Total: {totalCount}
                                    </span>
                                    <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs font-bold ring-1 ring-red-500/20 flex items-center gap-1">
                                        <FaExclamationTriangle size={10} /> Pending: {pendingCount}
                                    </span>
                                </div>
                                {selectedMember || startDate || endDate || startDateInvalid || endDateInvalid ? (
                                    <span className="block mt-2">
                                        Showing results for:{" "}
                                        <span className="text-[#133A69] font-bold">
                                            {selectedMember?.Name_name || "All Members"}
                                        </span>
                                        {(startDate || endDate) && (
                                            <span>
                                                {" "}from{" "}
                                                <span className="text-[#133A69] font-bold">
                                                    {startDate ? moment(startDate).format("MMM DD, YYYY") : "Start"}
                                                </span>{" "}to{" "}
                                                <span className="text-[#133A69] font-bold">
                                                    {endDate ? moment(endDate).format("MMM DD, YYYY") : "End"}
                                                </span>
                                            </span>
                                        )}
                                    </span>
                                ) : (
                                    <span className="text-gray-400 block mt-2">All tasks are currently shown</span>
                                )}
                            </div>
                            {(selectedMember || startDate || endDate || startDateInvalid || endDateInvalid) && (
                                <button
                                    onClick={() => {
                                        setSelectedMember(null);
                                        setStartDate("");
                                        setEndDate("");
                                        setStartDateInvalid(false);
                                        setEndDateInvalid(false);
                                        dispatch(DayReportFindforcheq());
                                        setRefreshActivity((prev) => prev + 1);
                                    }}
                                    className="bg-red-50 text-red-600 px-3 py-1 rounded text-sm font-semibold hover:bg-red-100 transition-colors border border-red-200"
                                >
                                    Reset Filters / Show All
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Tab Navigation */}
            <div className="flex gap-4 mb-2 border-b border-gray-200 pb-1">
                <button
                    onClick={() => setActiveTab("tasks")}
                    className={`px-6 py-2 rounded-t-lg font-bold transition-all relative top-[1px] border border-b-0 ${activeTab === "tasks"
                        ? "bg-[#133A69] text-white shadow-md border-[#133A69]"
                        : "bg-gray-200 text-gray-600 hover:bg-gray-300 border-gray-300"
                        }`}
                >
                    <div className="flex items-center gap-2">
                        <FaTasks />
                        Task Reports
                    </div>
                </button>
                <button
                    onClick={() => setActiveTab("activity")}
                    className={`px-6 py-2 rounded-t-lg font-bold transition-all relative top-[1px] border border-b-0 ${activeTab === "activity"
                        ? "bg-[#133A69] text-white shadow-md border-[#133A69]"
                        : "bg-gray-200 text-gray-600 hover:bg-gray-300 border-gray-300"
                        }`}
                >
                    <div className="flex items-center gap-2">
                        <FaFileAlt />
                        Activity Details
                    </div>
                </button>
            </div>

            {/* Main Tasks Table */}
            {activeTab === "tasks" && (
                <div className="bg-white shadow-xl rounded-2xl rounded-tl-none overflow-hidden border border-gray-100 relative z-0">
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                        <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                            <FaTasks className="text-[#133A69]" />
                            Task Reports
                        </h4>
                        <span className="bg-[#133A69] text-white px-3 py-1 rounded-full text-xs font-medium">
                            {totalCount} Tasks
                        </span>
                    </div>

                    <div className="w-full overflow-x-auto">
                        <div className="min-w-full overflow-hidden relative z-0">
                            <div className="max-h-[850px] overflow-y-auto relative scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                <table className="min-w-full table-auto border-collapse divide-y divide-gray-200 text-sm">
                                    <thead className="bg-[#133A69] text-white sticky top-0 z-50">
                                        <tr>
                                            <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "60px" }}>
                                                NO
                                            </th>
                                            <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "150px" }}>
                                                MEMBER NAME
                                            </th>
                                            <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "120px" }}>
                                                DATE
                                            </th>
                                            <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "200px" }}>
                                                COMPANY
                                            </th>
                                            <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "120px" }}>
                                                CALL NO.
                                            </th>
                                            <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "250px" }}>
                                                DESCRIPTION
                                            </th>
                                            <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "120px" }}>
                                                INTIMATION
                                            </th>
                                            <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "120px" }}>
                                                TASK DEMO
                                            </th>
                                            <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "200px" }}>
                                                REMARK
                                            </th>
                                            <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "120px" }}>
                                                ACTION
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 bg-white">
                                        {filteredTasks.length === 0 ? (
                                            <tr>
                                                <td colSpan={10} className="text-center border-none h-[400px]">
                                                    <div className="flex flex-col items-center justify-center h-full">
                                                        <div className="w-16 h-16 bg-[#F0F7FF] rounded-full flex items-center justify-center mb-4 shadow-sm border border-[#E8F2FF]">
                                                            <svg className="w-8 h-8 text-[#133a69]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                                            </svg>
                                                        </div>
                                                        <h3 className="text-base font-bold text-[#133a69] mb-1 tracking-wide">No Data Found</h3>
                                                        <p className="text-gray-500 text-sm text-center max-w-[280px] leading-relaxed">
                                                            We couldn't find any records matching your filters.
                                                        </p>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredTasks
                                                .slice(safeTaskPage * taskRowsPerPage, safeTaskPage * taskRowsPerPage + taskRowsPerPage)
                                                .map((task, index) => (
                                                    <tr
                                                        key={task.id || index}
                                                        className={`group transition-all duration-200 hover:shadow-md ${getRowColor(task.verifyStatus)}`}
                                                    >
                                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                                            <div className="text-sm font-bold text-gray-500">
                                                                {safeTaskPage * taskRowsPerPage + index + 1}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center">
                                                                <div className="flex-shrink-0 h-9 w-9 mr-3">
                                                                    {task.profilePhoto ? (
                                                                        <img
                                                                            src={task.profilePhoto}
                                                                            alt={task.memberName}
                                                                            className="h-9 w-9 rounded-full object-cover ring-2 ring-white shadow-sm"
                                                                        />
                                                                    ) : (
                                                                        <div className="h-9 w-9 rounded-full bg-indigo-100 flex items-center justify-center ring-2 ring-white shadow-sm">
                                                                            <FaUserCircle className="h-6 w-6 text-indigo-500" />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="text-sm font-semibold text-gray-800 group-hover:text-indigo-700 transition-colors">
                                                                    {task.memberName}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                                            <div className="text-sm font-medium text-gray-600 bg-white/50 px-2 py-1 rounded inline-block">
                                                                {task.displayDate || "-"}
                                                            </div>
                                                            <div className="text-xs text-gray-400 mt-1">
                                                                {task.displayTime || ""}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="text-sm font-medium text-gray-800">
                                                                {task.companyName}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                                            <button
                                                                onClick={() => handleCallClick(task)}
                                                                className="inline-flex items-center text-sm px-3 py-1 bg-[#112A47]/10 rounded-full font-medium text-[#112A47] hover:bg-[#112A47] hover:text-white transition-all duration-200 cursor-pointer"
                                                            >
                                                                <FaHashtag className="mr-1 text-xs" />
                                                                {String(task.callNo || "").replace("#", "")}
                                                            </button>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div
                                                                className="text-sm text-gray-600 leading-relaxed text-left w-full break-words whitespace-pre-wrap bg-gray-50 p-3 rounded-lg border border-gray-200 shadow-sm min-h-[50px]"
                                                                title={task.description}
                                                            >
                                                                {task.description || "-"}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                                            <button
                                                                className={`mx-auto h-8 px-4 rounded-full text-xs font-bold transition-all duration-200 border shadow-sm flex items-center justify-center gap-1.5 cursor-pointer ${getVerifyButtonColor(
                                                                    task.technical === "Verified" ? "verified" : "verify"
                                                                )}`}
                                                                onClick={() => {
                                                                    setTaskData((prev) =>
                                                                        prev.map((t) =>
                                                                            t.id === task.id
                                                                                ? {
                                                                                    ...t,
                                                                                    technical:
                                                                                        t.technical === "Verified"
                                                                                            ? "Pending"
                                                                                            : "Verified",
                                                                                }
                                                                                : t
                                                                        )
                                                                    );
                                                                }}
                                                            >
                                                                {task.technical === "Verified" && (
                                                                    <FaRegCircleCheck className="text-sm" />
                                                                )}
                                                                {getVerifyButtonText(
                                                                    task.technical === "Verified" ? "verified" : "verify"
                                                                )}
                                                            </button>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                                            <button
                                                                onClick={() => handleVerifyClick(task.id)}
                                                                className={`mx-auto h-8 px-4 rounded-full text-xs font-bold transition-all duration-200 border shadow-sm flex items-center justify-center gap-1.5 cursor-pointer ${getVerifyButtonColor(
                                                                    task.verifyStatus
                                                                )}`}
                                                            >
                                                                {task.verifyStatus === "verified" && (
                                                                    <FaRegCircleCheck className="text-sm" />
                                                                )}
                                                                {getVerifyButtonText(task.verifyStatus)}
                                                            </button>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <input
                                                                type="text"
                                                                value={remarks[task.id] || task.Remark || ""}
                                                                onChange={(e) => handleRemarkChange(task.id, e.target.value)}
                                                                className="w-full px-3 py-2 bg-white/70 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 text-sm shadow-sm transition-all"
                                                                placeholder="Add remark..."
                                                            />
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                                            <button
                                                                onClick={() => handleSubmit(task.id)}
                                                                disabled={
                                                                    task.action === "submitted" ||
                                                                    submittingIds.has(task.id) ||
                                                                    (task.technical !== "Verified" && task.verifyStatus !== "verified")
                                                                }
                                                                title={
                                                                    submittingIds.has(task.id)
                                                                        ? "Submitting task..."
                                                                        : task.action === "submitted"
                                                                            ? "Already submitted"
                                                                            : task.technical !== "Verified" && task.verifyStatus !== "verified"
                                                                                ? "Verify at least one status (Intimation or Task Demo) to submit"
                                                                                : "Submit Task"
                                                                }
                                                                className={`h-8 px-4 rounded-lg text-xs font-bold uppercase tracking-wide transition-all duration-200 shadow-sm ${task.action === "submitted" || submittingIds.has(task.id)
                                                                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                                                    : task.technical !== "Verified" && task.verifyStatus !== "verified"
                                                                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                                                        : "bg-[#112A47] text-white hover:bg-[#1a3b61] hover:shadow-md hover:-translate-y-0.5 cursor-pointer"
                                                                    }`}
                                                            >
                                                                {submittingIds.has(task.id)
                                                                    ? "Submitting..."
                                                                    : task.action === "submitted"
                                                                        ? "Done"
                                                                        : "Submit"}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Pagination for Tasks Table */}
                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={safeTaskPage}
                        onPageChange={handleChangeTaskPage}
                        rowsPerPage={taskRowsPerPage}
                        onRowsPerPageChange={handleChangeTaskRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        className="bg-gray-50 border-t border-gray-200"
                    />
                </div>
            )}

            {/* Activity Details Tab */}
            {activeTab === "activity" && (
                <AllTaskReport
                    selectedMember={selectedMember}
                    startDate={startDate}
                    endDate={endDate}
                    filterDateType={filterDateType}
                    startDateInvalid={startDateInvalid}
                    endDateInvalid={endDateInvalid}
                    refreshActivity={refreshActivity}
                />
            )}

            {/* Activity Details Modal */}
            {modalOpen && (
                <div
                    className="fixed top-0 left-0 w-screen h-screen z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
                    onClick={closeModal}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="bg-[#F4F7FB] rounded-xl shadow-2xl w-full max-w-2xl h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200"
                    >
                        {DayReportFindforcheqReportLoading ? (
                            <div className="flex justify-center items-center py-20 bg-white flex-1">
                                <Loader />
                            </div>
                        ) : selectedCallDetails && Array.isArray(selectedCallDetails.data) && selectedCallDetails.data.length > 0 ? (
                            <>
                                {/* Dark Blue Header Section */}
                                <div className="bg-[#112A47] text-white p-6 pb-8">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="bg-white/10 px-3 py-1.5 rounded flex items-center gap-2">
                                                <FaHashtag className="text-sm opacity-70" />
                                                <span className="font-bold text-lg tracking-wide">
                                                    {selectedCallDetails?.data?.[0]?.WorkId?.CallNo || "0000"}
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    const workId = selectedCallDetails?.data?.[0]?.WorkId?._id;
                                                    if (workId) {
                                                        window.open(`/InhouseRnd/CustomerrequustDetail/${workId}`, "_blank");
                                                    }
                                                }}
                                                className="flex items-center gap-1 text-xs bg-white/10 hover:bg-white/20 px-2 py-1.5 rounded transition-all text-blue-100 cursor-pointer"
                                            >
                                                View <FaExternalLinkAlt size={10} />
                                            </button>
                                        </div>
                                        <button
                                            onClick={closeModal}
                                            className="text-white/60 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full cursor-pointer"
                                        >
                                            <FaTimes size={18} />
                                        </button>
                                    </div>

                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="relative">
                                            <div className="w-14 h-14 rounded-full border-2 border-white/20 p-0.5 overflow-hidden bg-white/5 shrink-0">
                                                <img
                                                    src={
                                                        selectedCallDetails?.data?.[0]?.Memberid?.ProfilePhoto ||
                                                        "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                                                    }
                                                    alt="Profile"
                                                    className="w-full h-full rounded-full object-cover"
                                                    onError={(e) => {
                                                        e.target.src =
                                                            "https://cdn-icons-png.flaticon.com/512/149/149071.png";
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-lg text-white leading-tight mb-1">
                                                {selectedCallDetails?.data?.[0]?.Memberid?.Name || "Unknown Member"}
                                            </h4>
                                            <div className="flex items-center gap-2 text-white/60 text-xs font-medium">
                                                <FaBuilding size={10} />
                                                <span>
                                                    {typeof selectedCallDetails?.data?.[0]?.WorkId?.CompanyName === "object"
                                                        ? selectedCallDetails?.data?.[0]?.WorkId?.CompanyName?.WhaClientName ||
                                                        selectedCallDetails?.data?.[0]?.WorkId?.CompanyName?.CompanyName
                                                        : selectedCallDetails?.data?.[0]?.WorkId?.CompanyName || "Unknown Company"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Info Cards inside Blue Header */}
                                    <div className="space-y-3">
                                        <div className="bg-[#1A365D] rounded-lg p-3 border border-white/10">
                                            <div className="flex items-center gap-2 text-xs font-bold text-white/70 uppercase tracking-wider mb-1">
                                                <FaBriefcase /> Benefit / Requirement
                                            </div>
                                            <div className="text-sm font-medium text-white pl-5">
                                                {selectedCallDetails?.data?.[0]?.WorkId?.Requirement ||
                                                    selectedCallDetails?.data?.[0]?.WorkId?.Benefit ||
                                                    "-"}
                                            </div>
                                        </div>
                                        <div className="bg-[#1A365D] rounded-lg p-3 border border-white/10">
                                            <div className="flex items-center gap-2 text-xs font-bold text-white/70 uppercase tracking-wider mb-1">
                                                <FaFileAlt /> Description
                                            </div>
                                            <div className="text-sm font-medium text-white pl-5">
                                                {(() => {
                                                    const validList = selectedCallDetails.data.filter((i) => i && i.GeneratedDate);
                                                    if (validList.length === 0) return selectedCallDetails.data[0]?.Description || "-";
                                                    const latest = validList.reduce((acc, cur) =>
                                                        new Date(cur.GeneratedDate) > new Date(acc.GeneratedDate) ? cur : acc,
                                                        validList[0]
                                                    );
                                                    return latest?.Description || "-";
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Timeline Body */}
                                <div className="p-6 overflow-y-auto custom-scrollbar flex-1 bg-[#F4F7FB]">
                                    <div className="flex justify-between items-center mb-4">
                                        <h4 className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
                                            <FaHistory /> Activity Timeline
                                        </h4>
                                        {(() => {
                                            const grouped = getGroupedActivities();
                                            const validDates = Object.keys(grouped)
                                                .filter((d) => d !== "unknown" && moment(d).isValid())
                                                .map((date) => moment(date));
                                            if (validDates.length > 0) {
                                                const minDate = moment.min(validDates);
                                                const maxDate = moment.max(validDates);
                                                const daysDiff = Math.max(1, maxDate.diff(minDate, "days") + 1);
                                                return (
                                                    <span className="bg-indigo-50 text-indigo-600 text-xs font-bold px-3 py-1 rounded-full border border-indigo-100">
                                                        Active for {daysDiff} day{daysDiff !== 1 ? "s" : ""}
                                                    </span>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </div>

                                    <div className="space-y-4">
                                        {(() => {
                                            const grouped = getGroupedActivities();
                                            const sortedDates = Object.keys(grouped).sort((a, b) => moment(b).diff(moment(a)));

                                            if (sortedDates.length === 0) {
                                                return (
                                                    <div className="text-center py-10 text-gray-400 text-sm italic">
                                                        No activity history found.
                                                    </div>
                                                );
                                            }

                                            return sortedDates.map((date) => (
                                                <div key={date} className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
                                                    <button
                                                        onClick={() => toggleDay(date)}
                                                        className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                                                    >
                                                        <div className="flex flex-col items-start gap-1">
                                                            <span className="font-bold text-gray-800 text-base">
                                                                {date !== "unknown" && moment(date).isValid()
                                                                    ? moment(date).format("dddd, MMM DD, YYYY")
                                                                    : "Date Unknown"}
                                                            </span>
                                                            <span className="text-xs text-gray-500 font-medium bg-gray-100 px-2 py-0.5 rounded-full">
                                                                {grouped[date]?.length || 0} activities
                                                            </span>
                                                        </div>
                                                        {expandedDays[date] ? (
                                                            <FaChevronUp className="text-gray-400" />
                                                        ) : (
                                                            <FaChevronDown className="text-gray-400" />
                                                        )}
                                                    </button>

                                                    {expandedDays[date] && (
                                                        <div className="border-t border-gray-100 bg-gray-50/30 p-4 space-y-3">
                                                            {grouped[date]?.map((activity, idx) => (
                                                                <div
                                                                    key={activity?._id || idx}
                                                                    className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow"
                                                                >
                                                                    <div className="flex items-center gap-2 text-xs font-bold text-indigo-600 mb-2">
                                                                        <span>
                                                                            {activity?.GeneratedDate && moment(activity.GeneratedDate).isValid()
                                                                                ? moment(activity.GeneratedDate).utc().format("hh:mm A")
                                                                                : ""}
                                                                        </span>
                                                                        <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                                                                        <span className="text-gray-500 font-medium">
                                                                            {activity?.GeneratedDate && moment(activity.GeneratedDate).isValid()
                                                                                ? moment(activity.GeneratedDate).utc().format("DD MMM, YYYY")
                                                                                : ""}
                                                                        </span>
                                                                    </div>
                                                                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                                                                        {activity?.Description || "-"}
                                                                    </p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full py-20 flex-1">
                                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                    <FaHistory className="text-gray-400 text-3xl" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-700 mb-2">No History Found</h3>
                                <p className="text-gray-500 text-sm">We couldn't find any activity history for this task.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Wrap with ErrorBoundary to guarantee the page never breaks into a blank white screen
const TaskTimeingReport = () => (
    <ErrorBoundary>
        <TaskTimeingReportContent />
    </ErrorBoundary>
);

export default TaskTimeingReport;